import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { SortDirection, SortDirections } from "~/modules/filters/types";
import { SortInput } from "../SortInput";
import { Tooltip } from "../Tooltip";
import { RowSize, RowSizes } from "./types";
import styles from "./styles.module.css";
import {
    faArrowRight,
    faBoltLightning,
    faLock,
} from "@fortawesome/pro-solid-svg-icons";
import { Link } from "../Link";
import { Routes } from "~/routes";
import { DataGridUserEvents } from "~/modules/analytics/events/dataGrid";
import { useRegistrationModal } from "~/modules/auth/hooks/useRegistrationModal";
import { isCatalogSlug } from "~/modules/catalog/lib/isCatalogSlug";
import { CatalogTooltip } from "~/modules/catalog/components/CatalogTooltip";

// // // //

interface Props {
    label: string | React.ReactNode;
    slug: string;
    width: number;
    maxWidth?: number;
    tooltip?: string;
    className?: string;
    loading?: boolean;
    contentClassName?: string;
    size?: RowSize;
    direction?: SortDirection;
    scrollLeft?: number;
    flush?: boolean;
    isLoginGated?: boolean;
    isProGated?: boolean;
    analyticsTableName: string;
    sticky?: {
        left: number;
        isLast?: boolean;
    };
    onChangeSort?: (updatedDirection: SortDirection) => void;
}

const DIRECTIONS: SortDirection[] = [
    SortDirections.none,
    SortDirections.desc,
    SortDirections.asc,
];

export function DataGridHeaderCell(props: Props) {
    const {
        label,
        slug,
        width,
        maxWidth,
        tooltip = null,
        loading = false,
        className = "",
        contentClassName = "",
        direction = null,
        size = RowSizes.medium,
        scrollLeft = 0,
        onChangeSort,
        sticky = null,
        // flush = false, // NOTE - props.flush isn't used here at the moment
        isLoginGated = false,
        isProGated = false,
        analyticsTableName,
    } = props;

    const isStickyLeft = sticky !== null;
    const addBoxShadow = isStickyLeft && sticky.isLast && scrollLeft !== 0;
    const { open: openRegistrationModal } = useRegistrationModal();

    const sortable = direction !== null && onChangeSort !== undefined;

    const onClick =
        !sortable || isLoginGated || isProGated
            ? undefined
            : () => {
                  if (!sortable) {
                      return;
                  }
                  const idx = DIRECTIONS.indexOf(direction);
                  onChangeSort(DIRECTIONS[(idx + 1) % 3]);
              };

    const isExtraSmall = size === RowSizes.extraSmall;
    const showCompactLoginGateCta = isLoginGated && isExtraSmall;
    const showCompactProGateCta = isProGated && isExtraSmall;
    return (
        <th
            scope="col"
            onClick={onClick}
            data-testid={`data-grid-header--${slug}`}
            style={{
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: maxWidth !== undefined ? `${maxWidth}px` : undefined,
                left: isStickyLeft ? `${sticky.left}px` : undefined,
            }}
            className={cn(
                "flex grow px-4 text-left font-normal transition-colors text-slate-600 bg-muted",
                {
                    [className]: className !== "",
                    "flex-col justify-center flex-wrap":
                        isLoginGated || isProGated,
                    [contentClassName === "justify-end"
                        ? "items-end"
                        : "items-start"]: isLoginGated || isProGated,
                },
                {
                    "lg:sticky z-40": isStickyLeft,
                    [styles["sticky-left-shadow"]]: addBoxShadow,
                    "cursor-wait": loading,
                    "cursor-pointer group hover:text-slate-800":
                        !loading && sortable && !isLoginGated && !isProGated,
                },
                // NOTE - this changes the background color on hover when size is extra small
                // We hide the <SortInput /> on extra small size, so we we change the background color
                {
                    "hover:bg-slate-300":
                        size === RowSizes.extraSmall &&
                        sortable &&
                        !isLoginGated &&
                        !isProGated,
                },
                {
                    // Combining xs + sm sizes for now
                    // "py-1": size === RowSizes.extraSmall,
                    // "py-1.5": size === RowSizes.small,
                    "py-1.5":
                        size === RowSizes.small || size === RowSizes.extraSmall,
                    "py-3": size === RowSizes.medium,
                    "py-6": size === RowSizes.large,
                    "py-8": size === RowSizes.extraLarge,
                },
                {
                    "text-xs": size === RowSizes.extraSmall,
                    "text-sm": size === RowSizes.small,
                    "text-base": size === RowSizes.medium,
                    "text-xl": size === RowSizes.large,
                    "text-2xl": size === RowSizes.extraLarge,
                },
            )}
        >
            <div
                className={cn("w-full flex items-center gap-2", {
                    [contentClassName]: contentClassName !== "",
                })}
            >
                <span className="select-none overflow-hidden text-ellipsis hover:overflow-visible dg-overflow whitespace-nowrap">
                    {label}
                </span>

                {tooltip !== null &&
                    !isCatalogSlug(tooltip) &&
                    !showCompactLoginGateCta &&
                    !showCompactProGateCta && (
                        <Tooltip
                            placement="top-start"
                            tooltipContent={
                                <div className="px-3 py-2 text-sm max-w-160">
                                    {tooltip}
                                </div>
                            }
                        >
                            {({ setTriggerRef }) => (
                                <span ref={setTriggerRef}>
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        className="text-slate-500 hover:text-slate-700 transition-colors h-3 cursor-help"
                                    />
                                </span>
                            )}
                        </Tooltip>
                    )}

                {/* ENHANCEMENT - integrate CatalogTooltip */}
                {tooltip !== null &&
                    isCatalogSlug(tooltip) &&
                    !showCompactLoginGateCta &&
                    !showCompactProGateCta && (
                        <CatalogTooltip
                            slug={tooltip}
                            placement="top-start"
                            iconClassName="text-slate-500 hover:text-slate-700 transition-colors h-3 cursor-help"
                            analyticsEventProperties={{
                                tableName: analyticsTableName,
                            }}
                        />
                    )}

                {showCompactLoginGateCta && (
                    <Tooltip
                        placement="top-start"
                        tooltipContent={
                            <div className="w-max px-3 py-2">
                                <p className="whitespace-nowrap text-sm text-center flex items-center">
                                    Create a free RWA.xyz account to view this
                                    data
                                </p>
                                <p className="whitespace-nowrap text-sm text-center flex items-center text-blue-200 gap-1.5">
                                    Click to log in
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="h-2.5 w-2.5"
                                    />
                                </p>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <button
                                ref={setTriggerRef}
                                type="button"
                                onClick={() => {
                                    openRegistrationModal({
                                        label: DataGridUserEvents.ClickLoginToUnlock,
                                        properties: {
                                            column: slug,
                                            tableName: analyticsTableName,
                                        },
                                    });
                                }}
                                className="cursor-pointer text-link hover:text-link-hover transition-colors"
                            >
                                <FontAwesomeIcon
                                    icon={faLock}
                                    className="h-2.5 w-2.5"
                                />
                            </button>
                        )}
                    </Tooltip>
                )}

                {showCompactProGateCta && (
                    <Tooltip
                        placement="top-start"
                        tooltipContent={
                            <div className="w-max px-3 py-2">
                                <p className="whitespace-nowrap text-sm text-center flex items-center">
                                    Upgrade to Pro to view this data
                                </p>
                                <p className="whitespace-nowrap text-sm text-center flex items-center text-blue-200 gap-1.5">
                                    View Pricing
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="h-2.5 w-2.5"
                                    />
                                </p>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <Link
                                href={Routes.salesPage}
                                analyticsLabel={
                                    DataGridUserEvents.ClickUpgradeToUnlock
                                }
                                analyticsEventProperties={{ column: slug }}
                                setTriggerRef={setTriggerRef}
                                variant="default"
                            >
                                <FontAwesomeIcon
                                    icon={faLock}
                                    className="h-2.5 w-2.5"
                                />
                            </Link>
                        )}
                    </Tooltip>
                )}

                {sortable && !isLoginGated && !isProGated && (
                    <SortInput direction={direction} size={size as RowSizes} />
                )}
            </div>

            {/* "Log in To Unlock" CTA */}
            {isLoginGated && !showCompactLoginGateCta && (
                <button
                    type="button"
                    onClick={() => {
                        openRegistrationModal({
                            label: DataGridUserEvents.ClickLoginToUnlock,
                            properties: {
                                column: slug,
                                tableName: analyticsTableName,
                            },
                        });
                    }}
                    className={cn(
                        "cursor-pointer text-link hover:text-link-hover transition-colors select-none text-xs flex items-center gap-1.5 font-semibold whitespace-nowrap w-fit mt-0.5",
                        {
                            "justify-end w-full":
                                contentClassName === "justify-end",
                            "w-fit": contentClassName !== "justify-end",
                        },
                    )}
                >
                    <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" />
                    Log in to unlock
                </button>
            )}

            {/* "Upgrade to Pro" CTA */}
            {/* ENHANCEMENT - try making this a stripe checkout link */}
            {isProGated && !showCompactProGateCta && (
                <Link
                    href={Routes.salesPage}
                    analyticsLabel={DataGridUserEvents.ClickUpgradeToUnlock}
                    analyticsEventProperties={{ column: slug }}
                    variant="default"
                    className={cn(
                        "select-none text-xs flex items-center gap-1.5 font-semibold whitespace-nowrap w-fit mt-0.5",
                        {
                            "justify-end w-full":
                                contentClassName === "justify-end",
                            "w-fit": contentClassName !== "justify-end",
                        },
                    )}
                >
                    <FontAwesomeIcon
                        icon={faBoltLightning}
                        className="h-2.5 w-2.5 text-yellow-400"
                    />
                    Upgrade to Pro
                </Link>
            )}
        </th>
    );
}
